import { useEffect, useState } from 'react'
import { Button, Text, Flex } from '@radix-ui/themes'
import { useTranslation } from 'react-i18next'

interface TradingChartProps {
  symbol: string
}

const CG_IDS: Record<string, string> = {
  BTC: 'bitcoin',
  ETH: 'ethereum',
  SOL: 'solana',
  USDT: 'tether',
  BNB: 'binancecoin',
  USDC: 'usd-coin',
  BUSD: 'binance-usd',
  MATIC: 'polygon-ecosystem-token',
  POL: 'polygon-ecosystem-token',
  DAI: 'dai',
  WETH: 'weth',
  WBNB: 'wbnb',
  LINK: 'chainlink',
  CAKE: 'pancakeswap-token',
  BTCB: 'binance-bitcoin',
  UNI: 'uniswap',
  SHIB: 'shiba-inu',
  AAVE: 'aave',
  AVAX: 'avalanche-2',
  DOGE: 'dogecoin',
  DOT: 'polkadot',
  ADA: 'cardano',
  ARB: 'arbitrum',
  OP: 'optimism',
  XRP: 'ripple',
  LTC: 'litecoin',
  TRX: 'tron',
  TON: 'the-open-network'
}

const TradingChart = ({ symbol }: TradingChartProps) => {
  const { t } = useTranslation()
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains('dark')
  )

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'))
    })
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })
    return () => observer.disconnect()
  }, [])

  // If it's OMDB, render the Coinstore card
  if (symbol === 'OMDB' || symbol === 'OMD') {
    return (
      <div
        style={{
          width: '100%',
          height: 350,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'var(--color-bg-card)',
          borderRadius: '24px',
          border: '1px solid var(--color-border-base)',
          marginTop: '20px',
          padding: '20px'
        }}
      >
        <Flex
          direction="column"
          align="center"
          gap="4"
          style={{ textAlign: 'center' }}
        >
          <Text
            size="5"
            weight="bold"
            style={{ color: 'var(--color-text-main)' }}
          >
            {t('trading.exclusive_trading', { symbol })}
          </Text>
          <Text
            size="3"
            style={{
              color: 'var(--color-text-secondary)',
              maxWidth: '320px',
              lineHeight: '1.5'
            }}
          >
            {t('trading.coinstore_desc')}
          </Text>
          <Button
            size="3"
            variant="solid"
            className="btn-gradient-fill hover:opacity-90 transition-opacity"
            style={{
              marginTop: '15px',
              color: 'black',
              fontWeight: 'bold',
              borderRadius: '14px',
              padding: '0 30px',
              cursor: 'pointer'
            }}
            onClick={() =>
              window.open(
                `https://www.coinstore.com/#/spot/${symbol === 'OMD' ? 'OMDB' : symbol}USDT`,
                '_blank'
              )
            }
          >
            {t('trading.trade_coinstore')}
          </Button>
        </Flex>
      </div>
    )
  }

  // Use explicit mapping, or fallback to lowercase symbol name for CoinGecko
  const coinId = CG_IDS[symbol] || symbol.toLowerCase()

  const GeckoWidget = 'gecko-coin-price-chart-widget' as any

  // Render CoinGecko Widget for supported coins
  return (
    <div
      style={{
        width: '100%',
        height: 350,
        overflow: 'hidden',
        marginTop: '20px'
      }}
    >
      <GeckoWidget
        locale="en"
        outlined="true"
        coin-id={coinId}
        initial-currency="usd"
        transparent-background="false"
        dark-mode={isDark ? 'true' : 'false'}
      ></GeckoWidget>
    </div>
  )
}

export default TradingChart
